import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Helper function to normalize image URLs
// Converts relative /api/uploads/... paths to full URLs
export const normalizeImageUrl = (url) => {
  if (!url) return "";
  
  // If already a full URL, return as is
  if (url.startsWith("http://") || url.startsWith("https://")) {
    return url;
  }
  
  // If it's a relative /api/uploads path, prepend backend URL
  if (url.startsWith("/api/")) {
    return `${BACKEND_URL}${url}`;
  }
  
  // If it starts with just /uploads, add /api prefix
  if (url.startsWith("/uploads/")) {
    return `${BACKEND_URL}/api${url}`;
  }
  
  return url;
};

// Create axios instance with default config
const apiClient = axios.create({
  baseURL: API,
  headers: {
    "Content-Type": "application/json",
  },
});

// ============== EVENTS API ==============
export const eventsApi = {
  getAll: async (params = {}) => {
    const response = await apiClient.get("/events", { params });
    return response.data;
  },

  getFeatured: async () => {
    const response = await apiClient.get("/events/featured");
    return response.data;
  },

  getById: async (eventId) => {
    const response = await apiClient.get(`/events/${eventId}`);
    return response.data;
  },

  create: async (eventData) => {
    const response = await apiClient.post("/events", eventData);
    return response.data;
  },

  update: async (eventId, eventData) => {
    const response = await apiClient.put(`/events/${eventId}`, eventData);
    return response.data;
  },

  delete: async (eventId) => {
    const response = await apiClient.delete(`/events/${eventId}`);
    return response.data;
  },

  getAvailableSlots: async (eventId, date) => {
    const response = await apiClient.get(`/events/${eventId}/available-slots`, {
      params: { date },
    });
    return response.data;
  },

  getAvailability: async (eventId, date = null) => {
    const params = date ? { date } : {};
    const response = await apiClient.get(`/events/${eventId}/availability`, { params });
    return response.data;
  },
};

// ============== BOOKINGS API ==============
export const bookingsApi = {
  getAll: async (params = {}) => {
    const response = await apiClient.get("/bookings", { params });
    return response.data;
  },

  getById: async (bookingId) => {
    const response = await apiClient.get(`/bookings/${bookingId}`);
    return response.data;
  },

  create: async (bookingData) => {
    const response = await apiClient.post("/bookings", bookingData);
    return response.data;
  },

  confirm: async (bookingId, paymentMethod = "paypal") => {
    const response = await apiClient.put(
      `/bookings/${bookingId}/confirm?payment_method=${paymentMethod}`
    );
    return response.data;
  },

  cancel: async (bookingId) => {
    const response = await apiClient.put(`/bookings/${bookingId}/cancel`);
    return response.data;
  },

  checkIn: async (bookingId) => {
    const response = await apiClient.put(`/bookings/${bookingId}/checkin`);
    return response.data;
  },
};

// ============== ADMIN API ==============
export const adminApi = {
  login: async (credentials) => {
    const response = await apiClient.post("/admin/login", credentials);
    return response.data;
  },

  getDashboardStats: async () => {
    const response = await apiClient.get("/admin/dashboard");
    return response.data;
  },
};

// ============== PAYMENTS API ==============
export const paymentsApi = {
  createPayPalOrder: async (bookingId) => {
    const response = await apiClient.post(
      `/payments/create-paypal-order?booking_id=${bookingId}`
    );
    return response.data;
  },
};

// ============== QR VERIFICATION API ==============
export const verifyQR = async (bookingId) => {
  const response = await apiClient.get(`/verify-qr/${bookingId}`);
  return response.data;
};

export default apiClient;
